import { toFlag } from 'pano-utils';
import type { CFArticle } from '@types';
import { Collapse } from '@components/Collapse';
import { Validator } from '@components/Validator';
import Button from '@components/Button';
import FeatureToggle from '@components/FeatureToggle';
import Tip from '@components/Tip';
import SortableList from '@components/SortableList';
import arrayMove from '@utils/arrayMove';
import useEventWatcher from '@hooks/useEventWatcher';
import { Dropdown } from '@components/Dropdown';
import ModalSizeSetting from '@modules/panel/components/ModalSizeSetting';
import {
  defaultModalSize,
  modalSizeTypeItems,
  openStyleItems,
} from '@modules/panel/constants';
import { useCustomFeature } from '../../CustomFeatureContext';
import { onArticlesSelected, selectLibArticle } from '../../../mediaLib';
import CArticleItem from './CArticleItem';

const MAX = 10;

function CArticleSetting() {
  const { data, setData } = useCustomFeature<CFArticle>();
  const { articles = [] } = data;

  useEventWatcher(onArticlesSelected, ({ libItems }) => {
    if (!libItems) return;
    if (libItems.length + articles.length > MAX) {
      libItems.length = MAX - articles.length;
    }
    setData((draft) => {
      libItems.forEach((item) => {
        draft.articles.push(item.id);
      });
    });
  });

  const handleAddArticle = async () => {
    await selectLibArticle({ multiple: true });
  };

  const renderItem = (id: number, i: number) => (
    <CArticleItem
      key={`${i}-${id}`}
      id={id}
      index={i}
      listIndex={i}
      onDelete={(index) => {
        setData((draft) => {
          draft.articles.splice(index, 1);
        });
      }}
    />
  );

  return (
    <>
      <Collapse title="文章设置">
        <Button className="w-full" onClick={handleAddArticle}>
          添加文章
        </Button>
        <div className="mt-20 mb-10 flex">
          <div>文章列表</div>
          <div className="ml-10 self-end text-black-gray">{`${articles.length}/${MAX}`}</div>
        </div>
        <Validator
          value={articles}
          validFn={(value) => value.length > 0}
          warningMessage="请添加文章"
        >
          {({ value }) => {
            return value.length ? (
              <SortableList
                axis="y"
                lockAxis="y"
                distance={10}
                onSortEnd={(sort) => {
                  setData((draft) => {
                    draft.articles = arrayMove({
                      array: draft.articles,
                      fromIndex: sort.oldIndex,
                      toIndex: sort.newIndex,
                    });
                  });
                }}
              >
                <div>{value.map(renderItem)}</div>
              </SortableList>
            ) : (
              <>
                <Tip className="text-base">还未创建文章</Tip>
                <Tip className="text-base">{`最多可创建${MAX}篇文章`} </Tip>
              </>
            );
          }}
        </Validator>
      </Collapse>
      <Collapse title="播放控制">
        <FeatureToggle
          title="播放时关闭背景音乐"
          isOn={!!data.turnOffBgMusicOnPlay}
          onToggle={(val) =>
            setData((draft) => {
              draft.turnOffBgMusicOnPlay = toFlag(val);
            })
          }
        />
        <div className="mt-20 mb-10">弹出层样式：</div>
        <Dropdown
          items={openStyleItems}
          onChange={(typeId) => {
            setData((draft) => {
              draft.openStyle = typeId;
            });
          }}
          selected={data.openStyle ?? 1}
        />
        <div className="mt-20 mb-10">弹出层尺寸：</div>
        <Dropdown
          items={modalSizeTypeItems}
          onChange={(id) => {
            setData((draft) => {
              draft.modalSizeType = id;
              if (id === 1) {
                draft.size = defaultModalSize;
              }
            });
          }}
          selected={data.modalSizeType ?? 0}
        />
        {data.modalSizeType === 1 && (
          <ModalSizeSetting
            currentModalSize={data.size ?? defaultModalSize}
            onChange={(size) => {
              setData((draft) => {
                draft.size = size;
              });
            }}
          />
        )}
      </Collapse>
    </>
  );
}

export default CArticleSetting;
